import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, TextField } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { TranslateFunction } from '../../../hooks/useTranslate';
import { hideAlert, showAlert } from '../../../features/slices/applicationSlice';

const useAlerts = ({ translate }: { translate: TranslateFunction }) => {
    const dispatch = useDispatch();

    const showPresentVisitConfirmationAlert = ({ confirmAction }: any) => {
        dispatch(
            showAlert({
                title: translate('alerts.present.title'),
                message: translate('alerts.present.message'),
                icon: 'info',
                confirmText: translate('alerts.present.confirmText'),
                cancelText: translate('alerts.present.cancelText'),
                confirmAction: confirmAction
            })
        );
    };

    const showCancelVisitConfirmationAlert = ({
        confirmAction
    }: {
        confirmAction: (observacion: string) => void;
    }) => {
        const observacionRef = { current: '' };

        const ModalContent = () => {
            const [observacion, setObservacion] = useState('');

            return (
                <Box display="flex" flexDirection="column" gap={2} width="100%">
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        required
                        label={translate('alerts.cancelVisitAction.observacionLabel')}
                        value={observacion}
                        inputProps={{ maxLength: 500 }}
                        helperText={`${observacion.length}/500`}
                        onChange={(e) => {
                            observacionRef.current = e.target.value;
                            setObservacion(e.target.value);
                        }}
                    />
                </Box>
            );
        };

        const ModalButtons = () => {
            const handleConfirm = () => {
                const trimmedObservacion = observacionRef.current.trim();

                if (!trimmedObservacion) {
                    enqueueSnackbar(translate('alerts.cancelVisitAction.observacionRequired'), {
                        variant: 'warning'
                    });
                    return;
                }

                dispatch(hideAlert());
                confirmAction(trimmedObservacion);
            };

            const handleCancel = () => {
                dispatch(hideAlert());
            };

            return (
                <Box display="flex" gap={2} width="100%" justifyContent="center">
                    <Button variant="outlined" onClick={handleCancel}>
                        {translate('alerts.cancelVisitAction.cancelText')}
                    </Button>
                    <Button variant="contained" onClick={handleConfirm}>
                        {translate('alerts.cancelVisitAction.confirmText')}
                    </Button>
                </Box>
            );
        };

        dispatch(
            showAlert({
                title: translate('alerts.cancelVisitAction.title'),
                message: translate('alerts.cancelVisitAction.message'),
                icon: 'info',
                reactElement: <ModalContent />,
                customButtons: <ModalButtons />,
                keepMounted: true
            })
        );
    };

    const showRejectToRevisionConfirmationAlert = ({ confirmAction }: any) => {
        dispatch(
            showAlert({
                title: translate('alerts.rejectToRevision.title'),
                message: translate('alerts.rejectToRevision.message'),
                icon: 'info',
                confirmText: translate('alerts.rejectToRevision.confirmText'),
                cancelText: translate('alerts.rejectToRevision.cancelText'),
                confirmAction: confirmAction
            })
        );
    };

    return {
        showPresentVisitConfirmationAlert,
        showCancelVisitConfirmationAlert,
        showRejectToRevisionConfirmationAlert
    };
};

export default useAlerts;
